export const getErrorMessage = (err: unknown, fallback = "Something went wrong."): string => {
  const message = (err as { data?: { message?: unknown } })?.data?.message;
  // A validation 400 sends one message per failed rule, as an array.
  if (Array.isArray(message)) {
    const joined = message.filter((m) => typeof m === "string").join("; ");
    if (joined) return joined;
  } else if (typeof message === "string" && message) {
    return message;
  }
  return (err as { message?: string })?.message || fallback;
};

/**
 * HTTP status from an RTK Query / fetch error, if present.
 *
 * `status` is a number only for a plain HTTP failure. A response whose body
 * failed to parse as JSON — an nginx/WAF/gateway denial with an HTML body is
 * the common shape — comes back as `status: "PARSING_ERROR"` with the real
 * code on `originalStatus` instead; without this fallback that denial was
 * invisible to every caller of `isForbiddenError`.
 */
export const getApiErrorStatus = (err: unknown): number | undefined => {
  const status = (err as { status?: unknown })?.status;
  if (typeof status === "number") return status;
  if (status === "PARSING_ERROR") {
    const originalStatus = (err as { originalStatus?: unknown })
      ?.originalStatus;
    if (typeof originalStatus === "number") return originalStatus;
  }
  return undefined;
};

/** True when the error is a 403 (forbidden / out-of-scope). */
export const isForbiddenError = (err: unknown): boolean =>
  getApiErrorStatus(err) === 403;

/**
 * The API's stable error code, when the endpoint sends one.
 *
 * The exception filter puts the whole exception body on `errors`, so a code
 * thrown as `new NotFoundException({ message, errorCode })` arrives here.
 * Keying UI off this rather than the message text means the copy can be
 * reworded without silently dropping an affordance.
 */
export const getApiErrorCode = (err: unknown): string | undefined => {
  const code = (err as { data?: { errors?: { errorCode?: unknown } } })?.data
    ?.errors?.errorCode;
  return typeof code === "string" ? code : undefined;
};
